import { Url, Singleton } from 'app/utils';
import { Route } from 'app/classes/router/route';
import { Application } from 'app/classes/application/application';
import { $Window } from 'app/services/window.service/window.service';
import { $CurrentUser, $Analytics } from 'app/classes';

const SLASH = '/';
const AUTH = 'auth';
const HOME = 'launchpad/home/';
const LOGIN = `/${AUTH}/login/`;
const err404 = 'error-404';
const prohibitiveStatuses = ['suspended', 'seasonal-hold'];
const permittedStatuses = ['active', 'suspension-warning'];

interface IRouterGoOptions {
    replace?: boolean;
    overlay?: boolean;
}

export class Router {

    public apps: Map<string, Application> = new Map<string, Application>();
    public currentRoute: Route;
    public transitioningRoute: Route;
    public savedRoute: Route;

    constructor() {
        // Get the current route from the window location and save it.
        this.currentRoute = this.parse($Window.currentUrl());
        this.savedRoute = this.currentRoute;

        // Setup listener for when the user goes back/forward.
        $Window.onLocationChange((location) => {
            if (Url.wrapSlashes(location.href) !== this.currentRoute.fullpath) {
                const route = this.parse(location.href);
                this.go(route, {replace: true});
            }
        });
    }

    /**
     * Register an application into the collection of apps.
     *
     * @param {Application} app
     */
    public registerApp = (app: Application) => {
        this.apps.set(app.slug, app);
    };

    /**
     * Return a registered app by it's slug.
     *
     * @param {String} slug
     * @returns {Application}
     */
    public getAppBySlug = (slug: string): Application => {
        return this.apps.get(slug);
    };

    /**
     * Return the current Application
     */
    public getCurrentApplication = (): Application => {
        return this.getAppBySlug(this.currentRoute.slug);
    }

    /**
     * Return the transitioning Application
     */
    public getTransitioningApplication = (): Application => {
        if (this.transitioningRoute) {
            return this.getAppBySlug(this.transitioningRoute.slug);
        } else {
            return undefined;
        }
    }

    /**
     * Remove all privileged (user) applications from the collection of apps.
     */
    public unregisterApps = () => {
        this.apps.forEach((app, key) => {
            if (app.privileged && (app.slug !== 'launchpad')) {
                app.destroy();
                this.apps.delete(key);
            }
        });
    };

    /**
     * Send a message to every open application. Takes the same params as the
     * MessageBus.send() method.
     *
     */
    public broadcast = (...args) => {
        this.apps.forEach((app) => {
            if (!app.bus || !app.bus.send) { return; }
            app.bus.send.apply(this, args);
        });
    };

    /**
     * Save the current route to come back to later.
     */
    public saveRoute = () => {
        this.savedRoute = this.parse($Window.currentUrl());
    };

    /**
     * Go to the route that was saved, for redirecting user after login.
     */
    public gotoSavedRoute = () => {
        if ($CurrentUser.hasSession() && this.savedRoute.slug === AUTH) {
            this.goto(HOME);
        } else {
            this.go(this.savedRoute);
        }
    };

    /**
     * Show one of the error pages in an overlay fashion.
     *
     * @param {Number} code (403,404,500)
     */
    public showErrorPage = (code: number) => {
        this.goto(String('error-' + code), {overlay: true});
    };

    /**
     * Sugary syntax for preloading an app by it's slug.
     *
     * @param {String} slug
     */
    public preloadApp = (slug: string) => {
        this.preloadRoute(this.parse(slug));
    };

    /**
     * Attempt to preload the application for a given route.
     *
     * @param {Route} route
     */
    public preloadRoute = (route: Route) => {
        const app = this.getAppBySlug(route.slug);
        if (app && app.preload && !app.loaded) {
            app.load(route.deeplink);
        }
    };

    /**
     * Public method for transitioning to a new route. Path can be a relative
     * or absolute URL, with http|https protocol or not.
     *
     * Optional options are:
     *  replace: boolean - uses window.replaceState instead of pushState.
     *  overlay: boolean - does not change the location, used for errors.
     *
     * @param {string} path
     * @param {IRouterGoOptions} [options]
     */
    public goto = (path: string, options?: IRouterGoOptions) => {
        this.go(this.parse(path), options);
    };

    /**
     * Update the path based upon an application changing their own routes.
     * This is called by listening for the "appStateChange" post message.
     *
     * @param {String} path
     */
    public deeplink = (path: string) => {
        const route = this.parse(path);
        if (route.fullpath !== this.currentRoute.fullpath) {
            $Window.setLocation(route.fullpath, true);
            this.currentRoute = route;
        }
    };

    /**
     * Parse a path (either absolute URL or relative path) into a Route object.
     *
     * @param {String} path
     * @returns {Route}
     */
    public parse = (path: string) => {
        return new Route(path);
    };

    /**
     * Private method for transitioning to a new route.
     *
     * Determine application based on a given route and execute.
     *
     * Validate numerous params before executing the location change.
     *
     * @param {Route} route
     * @param {Object} [opts]
     * @returns {void}
     */
    public go = (route: Route, opts: IRouterGoOptions = {}) => {
        this.transitioningRoute = route;
        let app: Application = this.getRouteApp(route);

        // executes unregistered application request
        if (!app){
            this.executeUnregisteredAppRequest(route, app, opts);
            return;
        }

        // executes public application request
        if (app && app.public){
            this.executePublicAppRequest(route, app, opts);
            return;
        }

        // executes private application request from non-auth-ed users
        if (app && !app.public && !$CurrentUser.hasSession()){
            this.executePrivateAppRequest_nonAuthedUser(route, app, opts);
            return;
        }

        if (app && !app.public && $CurrentUser.hasSession() && !$CurrentUser.hasCorrectOrgToken()){
            this.executePrivateAppRequest_authedUser_wrongOrgToken(route, app, opts);
            return;
        }

        // executes private application request from auth-ed users
        if (app && !app.public && $CurrentUser.hasSession() && $CurrentUser.hasCorrectOrgToken()){
            this.executePrivateAppRequest_authedUser_correctOrgToken(route, app, opts);
            return;
        }

        // This should be unreachable code. The app will always either be
        // defined or not, and app.public will always be defined as a boolean
    };

    // Gets route for go method.
    // Handles logic for when the app slug is '/' and
    // when the app is in maintenance mode.
    //
    // The intention is to return an application that does
    // not need to be mutated through the rest of the go method.
    public getRouteApp = (route: Route): Application => {
        let app = this.getAppBySlug(route.slug);

        // Think about this
        // how does the app.slug / get registered?
        if ($CurrentUser.hasSession() && app && app.slug === '/'){
            if ($CurrentUser.apps && $CurrentUser.apps.length === 1){
                return $CurrentUser.apps[0];
            } else {
                const homeApp = this.parse(HOME);
                return this.getAppBySlug(homeApp.slug);
            }
        }

        //Maintenance Mode logic
        if (app && app.in_maintenance_mode && app.maintenance_url){
            app.url = app.maintenance_url;
            app.passToken = false;
            return app;
        }

        return app;
    }

    // Handles all routing for unregisterd app requests
    // This method should ONLY be called within the context of the go method
    // We could check all of the assumptions before we continue, if they fail,
    // we could send them back through the go method with the route and application.
    public executeUnregisteredAppRequest = (route: Route, app: Application, opts: IRouterGoOptions) => {
        if ($CurrentUser.hasSession()){
            // This protects us from an infinite redirect situation if
            // the error-404 app was not registered with the router.
            if (route.slug === err404){
                throw new Error('Unable to load error 404 page');
            }

            this.execute404();
            return;
        }

        this.executeLogin();
    };

    // Handles all routing for Public app requests
    // This method should ONLY be called within the context of the go method
    // We could check all of the assumptions before we continue, if they fail,
    // we could send them back through the go method with the route and application.
    public executePublicAppRequest = (route: Route, app: Application, opts: IRouterGoOptions) => {
        this.execute(route, app, opts);
    };

    // Handles all routing for private app requests from non auth-ed users
    // This method should ONLY be called within the context of the go method
    // We could check all of the assumptions before we continue, if they fail,
    // we could send them back through the go method with the route and application.
    public executePrivateAppRequest_nonAuthedUser = (route: Route, app: Application, opts: IRouterGoOptions) => {
        this.executeLogin();
    };

    public executePrivateAppRequest_authedUser_wrongOrgToken = (route: Route, app: Application, opts: IRouterGoOptions) => {
        const currentUrl = Url.getCurrent();
        const currentUrlObj = Url.parse(currentUrl);
        const searchObj = Url.parseSearch(currentUrlObj.search);
        const orgId = searchObj['cp.orgid'];
        const confirmLogoutRoute = new Route(`auth/confirm-logout?cp.orgid=${orgId}`);
        const authApp = this.getAppBySlug(confirmLogoutRoute.slug);
        this.execute(confirmLogoutRoute, authApp, opts);
    };

    // Handles all routing for private app requests from auth-ed users
    // This method should ONLY be called within the context of the go method
    // We could check all of the assumptions before we continue, if they fail,
    // we could send them back through the go method with the route and application.
    public executePrivateAppRequest_authedUser_correctOrgToken = (route: Route, app: Application, opts: IRouterGoOptions) => {
        // Handles probitive subscription statuses
        if (prohibitiveStatuses.indexOf(app.subscription.status) > -1){
            // @TODO: See if we can do this without an event.
            const elem = document.body;
            const event = new Event('launchAppWarningModal');
            elem.dispatchEvent(event);

            this.executeHome();
            return;
        }

        //Handles permitted subscription statuses and app is NOT in maintenance mode
        if (permittedStatuses.indexOf(app.subscription.status) > -1){
            // We do not have to check if it is maintenance mode because we have handled
            // that logic in getRouteApp.
            if (app.in_maintenance_mode && !app.maintenance_url){
                this.executeMaintenance();
                return;
            }

            this.execute(route, app, opts);
            return;
        }

        console.error("Application with invalid status - ", {app})
        this.executeHome();
    };

    // We should check at some point to make sure all of these applications
    // are loaded. That is one reason to send the request back through go.

    // Executes login route
    public executeLogin(){
        // Maintain query params, most relevant for org_id
        const currentUrl = Url.getCurrent();
        const {search} = Url.parse(currentUrl);
        const loginRoute = this.parse(LOGIN + search);
        const loginApp = this.getAppBySlug(loginRoute.slug);

        this.execute(loginRoute, loginApp);
    }

    // Executes 404 route
    public execute404(){
        const route404: Route = this.parse(err404);
        const app404: Application = this.getRouteApp(route404)
        this.execute(route404, app404, {overlay: true});
    }

    // Executes launchpad home route
    public executeHome(){
        const routeHome: Route = this.parse(HOME);
        const appHome: Application = this.getAppBySlug(routeHome.slug);
        this.execute(routeHome, appHome);
    }

    // Executes maintenance route
    public executeMaintenance(){
        const routeMaintenance: Route = this.parse('maintenance');
        const appMaintenance: Application = this.getAppBySlug(routeMaintenance.slug);
        this.execute(routeMaintenance, appMaintenance);
    }

    /**
     * Change the window location to match the supplied route and show the app.
     * The opts.replace boolean flag determines whether the window history is
     * pushed or replaced. Incorrect configuration of this results in back/forward
     * button misbehavior.
     *
     * @param {Route} route
     * @param {Application} app
     * @param {IRouterGoOptions} [opts]
     */
    public execute = (route: Route, app?: Application, opts: IRouterGoOptions = {}) => {
        this.currentRoute = route;
        this.transitioningRoute = undefined;
        if (!opts.overlay) {
            $Window.setLocation(route.fullpath, opts.replace);
        }

        if (app) {
            $Analytics.sendEvent(`launched-${app.name}`);
            this.hideAllAppsButOne(app);
            app.launch(route.deeplink).then(() => {
                if (app.slug === AUTH && this.savedRoute.slug === AUTH) {
                    this.preloadRoute(this.parse(HOME));
                } else {
                    this.preloadRoute(this.savedRoute);
                }
            });
        }
    };

    /**
     * Hides all apps, exluding the one specified.
     *
     * @param {Application} which
     */
    public hideAllAppsButOne = (which: Application) => {
        this.apps.forEach((app) => {
            if (which.id !== app.id) {
               app.hide();
           }
        });
    };

}

export let $Router = Singleton('$Router', Router);



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./public/app/classes/router/router.ts